<template>
  <div class="payment-blocked">
    <div class="payment-blocked__inner">
      <div class="payment-blocked__card">
        <div class="payment-blocked__icon-wrapper">
          <i class="fa-regular fa-clock-rotate-left payment-blocked__icon"></i>
        </div>

        <h1 class="payment-blocked__title">{{ $t('payment_blocked.title') }}</h1>
        <p class="payment-blocked__subtitle">{{ $t('payment_blocked.subtitle') }}</p>
        <p class="payment-blocked__hint">{{ $t('payment_blocked.hint') }}</p>
        <button
          class="payment-blocked__btn-support"
          @click="contactSupport"
        >
          <i class="fa-regular fa-headset"></i>
          {{ $t('payment_blocked.contact_support') }}
        </button>

        <div class="payment-blocked__check-status">
          <span v-if="isChecking" class="payment-blocked__check-dot payment-blocked__check-dot--active"></span>
          <span v-else class="payment-blocked__check-dot"></span>
          <span class="payment-blocked__check-label">
            {{ isChecking ? $t('payment_blocked.checking') : $t('payment_blocked.check_label') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const POLL_INTERVAL_MS = 60000

export default {
  name: 'PaymentBlocked',

  data() {
    return {
      isChecking: false,
      pollTimer: null
    }
  },

  mounted() {
    this.schedulePoll()
  },

  beforeDestroy() {
    this.clearPoll()
  },

  methods: {
    schedulePoll() {
      this.pollTimer = setTimeout(() => {
        this.checkStatus()
      }, POLL_INTERVAL_MS)
    },

    clearPoll() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },

    async checkStatus() {
      this.isChecking = true
      try {
        const token = localStorage.getItem('token')
        const res = await this.$http.get(
          `${process.env.VUE_APP_API_HOST}/users/me`,
          { headers: { Authorization: `Bearer ${token}` } }
        )

        if (res && res.data && res.data.user) {
          const user = res.data.user
          localStorage.setItem('user', JSON.stringify(user))

          // Payment issue resolved — redirect to dashboard
          if (!user._premium || !user._premium.needAddPaymentMethod) {
            window.location = '/#/dash'
            return
          }
        }
      } catch (e) {
        // Silent — keep user on this page
      } finally {
        this.isChecking = false
        this.schedulePoll()
      }
    },

    contactSupport() {
      if (window.$crisp) {
        window.$crisp.push(['do', 'chat:open'])
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$primary-color: #6b1f4d;
$secondary-color: #b64985;
$primary-text: #121826;
$secondary-text: #555e75;
$bg: #fbfafc;
$border: #e3dfe5;
$font: 'avenirnext', 'montserrat-regular', Helvetica, Roboto, Arial, sans-serif;

.payment-blocked {
  display: flex;
  min-height: 100vh;
  font-family: $font;
  background: $bg;
}

.payment-blocked__inner {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
}

.payment-blocked__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 420px;
}

.payment-blocked__icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba($primary-color, 0.08) 0%, rgba($secondary-color, 0.12) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
}

.payment-blocked__icon {
  font-size: 28px;
  color: $primary-color;
}

.payment-blocked__title {
  font-size: 22px;
  font-weight: 700;
  color: $primary-text;
  margin: 0 0 12px;
  line-height: 1.3;
}

.payment-blocked__subtitle {
  font-size: 15px;
  color: $secondary-text;
  line-height: 1.6;
  margin: 0 0 12px;
}

.payment-blocked__hint {
  font-size: 13px;
  color: lighten($secondary-text, 12%);
  line-height: 1.5;
  margin: 0 0 32px;
}

.payment-blocked__btn-support {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 24px;
  border-radius: 10px;
  border: 1.5px solid $border;
  background: #fff;
  color: $primary-text;
  font-family: $font;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  margin-bottom: 36px;

  i {
    font-size: 15px;
    color: $primary-color;
  }

  &:hover {
    border-color: $secondary-color;
    box-shadow: 0 2px 8px rgba($primary-color, 0.1);
  }
}

.payment-blocked__check-status {
  display: flex;
  align-items: center;
  gap: 7px;
}

.payment-blocked__check-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $border;
  flex-shrink: 0;
  transition: background 0.3s;

  &--active {
    background: #34c77b;
    animation: pulse-dot 1.2s ease-in-out infinite;
  }
}

.payment-blocked__check-label {
  font-size: 12px;
  color: lighten($secondary-text, 18%);
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.85); }
}
</style>
